import type { Feature, FeatureCollection, MultiPolygon, Point } from 'geojson'
import type { ViewState } from 'react-map-gl/mapbox'
import { getCenterZoomForBounds, getElementDimensions } from 'src/utils/map/viewportUtils'

/** Flatten MultiPolygon coordinates to [lng, lat] points. */
function getMultiPolygonPoints(coords: MultiPolygon['coordinates']): [number, number][] {
    const points: [number, number][] = []
    for (const polygon of coords) {
        for (const ring of polygon) {
            for (const pt of ring) {
                points.push([pt[0], pt[1]])
            }
        }
    }
    return points
}

function getGeoJsonPoints(geoJson: Feature<MultiPolygon> | FeatureCollection<MultiPolygon>): [number, number][] {
    return geoJson.type === 'FeatureCollection'
        ? geoJson.features.flatMap(f => getMultiPolygonPoints(f.geometry.coordinates))
        : getMultiPolygonPoints(geoJson.geometry.coordinates)
}

function getGeoJsonBounds(points: [number, number][]) {
    if (points.length === 0) {
        return null
    }

    let minLng = points[0][0]
    let maxLng = points[0][0]
    let minLat = points[0][1]
    let maxLat = points[0][1]
    for (const [lng, lat] of points) {
        if (lng < minLng) {
            minLng = lng
        }
        if (lng > maxLng) {
            maxLng = lng
        }
        if (lat < minLat) {
            minLat = lat
        }
        if (lat > maxLat) {
            maxLat = lat
        }
    }

    return { minLng, maxLng, minLat, maxLat }
}

/** Returns the bbox center of the geometry as { lat, lng }. */
export function getGeoJsonCenter(geoJson: Feature<MultiPolygon> | FeatureCollection<MultiPolygon>) {
    const bounds = getGeoJsonBounds(getGeoJsonPoints(geoJson))

    if (!bounds) {
        return { lat: 0, lng: 0 }
    }

    return {
        lng: (bounds.minLng + bounds.maxLng) / 2,
        lat: (bounds.minLat + bounds.maxLat) / 2,
    }
}

function getCenterZoomForGeoJson(
    geoJson: Feature<MultiPolygon> | FeatureCollection<MultiPolygon>,
    dimensions: [number, number],
): Pick<ViewState, 'latitude' | 'longitude' | 'zoom'> {
    const bounds = getGeoJsonBounds(getGeoJsonPoints(geoJson))

    if (!bounds) {
        return { latitude: 0, longitude: 0, zoom: 10 }
    }

    return getCenterZoomForBounds(bounds, dimensions)
}

/** Returns map view state that fits the geometry within the map container. */
export function getGeoJsonViewState(
    geoJson: Feature<MultiPolygon> | FeatureCollection<MultiPolygon>,
    mapWrapper: HTMLDivElement | undefined,
): Pick<ViewState, 'latitude' | 'longitude' | 'zoom'> {
    return getCenterZoomForGeoJson(geoJson, getElementDimensions(mapWrapper))
}

/** Returns map view state that fits the geometry within explicit map dimensions. */
export function getGeoJsonViewStateForDimensions(
    geoJson: Feature<MultiPolygon> | FeatureCollection<MultiPolygon>,
    width: number,
    height: number,
): Pick<ViewState, 'latitude' | 'longitude' | 'zoom'> {
    return getCenterZoomForGeoJson(geoJson, [width, height])
}

export function isValidGeoJsonFeatureCollection(geojson: FeatureCollection): boolean {
    if (geojson.type !== 'FeatureCollection') {
        return false
    }

    if (!geojson.features?.length) {
        return false
    }

    return geojson.features.every(isValidGeoJsonFeature)
}

export function isValidGeoJsonFeature(geojson: Feature): boolean {
    if (geojson.type !== 'Feature') {
        return false
    }

    if (!geojson.geometry) {
        return false
    }

    const { coordinates } = geojson.geometry as Point
    return !!coordinates?.length
}
